import { FilesetResolver, ImageSegmenter } from "@mediapipe/tasks-vision"
import {
  MEDIAPIPE_SELFIE_SEGMENTER_LANDSCAPE_URL,
  MEDIAPIPE_TASKS_VISION_BASE,
} from "../constants/mediapipe"

type WasmFileset = Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>

export function supportsBackgroundProcessor(): boolean {
  if (typeof window === "undefined") return false
  return (
    typeof MediaStreamTrackProcessor !== "undefined" &&
    typeof MediaStreamTrackGenerator !== "undefined" &&
    typeof VideoFrame !== "undefined" &&
    typeof OffscreenCanvas !== "undefined" &&
    typeof createImageBitmap !== "undefined" &&
    !!document.createElement("canvas").getContext("webgl2")
  )
}

let filesetPromise: Promise<WasmFileset> | null = null
let segmenterPromise: Promise<ImageSegmenter> | null = null
const bgBitmapCache = new Map<string, Promise<ImageBitmap>>()

async function getFileset(): Promise<WasmFileset> {
  if (!filesetPromise) {
    filesetPromise = FilesetResolver.forVisionTasks(
      MEDIAPIPE_TASKS_VISION_BASE,
    ).catch((err) => {
      filesetPromise = null
      throw err
    })
  }
  return filesetPromise
}

async function getSegmenter(): Promise<ImageSegmenter> {
  if (!segmenterPromise) {
    segmenterPromise = (async () => {
      const fileset = await getFileset()
      return ImageSegmenter.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: MEDIAPIPE_SELFIE_SEGMENTER_LANDSCAPE_URL,
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        outputCategoryMask: false,
        outputConfidenceMasks: true,
      })
    })().catch((err) => {
      segmenterPromise = null
      throw err
    })
  }
  return segmenterPromise
}

async function getBackgroundBitmap(url: string): Promise<ImageBitmap> {
  let cached = bgBitmapCache.get(url)
  if (!cached) {
    cached = (async () => {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.src = url
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error(`failed to load ${url}`))
      })
      return createImageBitmap(img)
    })().catch((err) => {
      bgBitmapCache.delete(url)
      throw err
    })
    bgBitmapCache.set(url, cached)
  }
  return cached
}

export async function prewarmBackgroundProcessor(
  imageUrl: string,
): Promise<void> {
  if (!supportsBackgroundProcessor()) return
  await Promise.all([getSegmenter(), getBackgroundBitmap(imageUrl)]).catch(
    () => {},
  )
}

const VERTEX_SHADER = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
void main() {
  v_uv = vec2(a_position.x * 0.5 + 0.5, 1.0 - (a_position.y * 0.5 + 0.5));
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`

const FRAGMENT_SHADER = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_frame;
uniform sampler2D u_mask;
uniform sampler2D u_bg;
out vec4 outColor;

// Joint Bilateral Upsampling. The segmenter outputs at 256×144; sampling that
// straight to HD shows staircase. JBU snaps the upsampled mask to image edges
// (hair, microphone, collar) by weighting each tap against both spatial
// distance AND color similarity to the current high-res pixel.
void main() {
  vec4 frame = texture(u_frame, v_uv);
  vec4 bg = texture(u_bg, v_uv);
  vec2 ts = 1.0 / vec2(textureSize(u_mask, 0));
  vec3 guide = frame.rgb;
  float maskSum = 0.0;
  float weightSum = 0.0;
  for (int dy = -2; dy <= 2; dy++) {
    for (int dx = -2; dx <= 2; dx++) {
      vec2 tapUv = v_uv + vec2(float(dx), float(dy)) * ts;
      float maskTap = texture(u_mask, tapUv).r;
      vec3 colorTap = texture(u_frame, tapUv).rgb;
      vec3 colorDiff = colorTap - guide;
      float spatial = exp(-float(dx * dx + dy * dy) / 8.0);
      float color = exp(-dot(colorDiff, colorDiff) / 0.05);
      float w = spatial * color;
      maskSum += w * maskTap;
      weightSum += w;
    }
  }
  float refined = maskSum / max(weightSum, 1e-4);
  float mask = smoothstep(0.6, 0.85, refined);
  outColor = mix(bg, frame, mask);
}
`

function compileShader(
  gl: WebGL2RenderingContext,
  type: GLenum,
  source: string,
): WebGLShader {
  const shader = gl.createShader(type)
  if (!shader) throw new Error("failed to allocate shader")
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader)
    gl.deleteShader(shader)
    throw new Error(`shader compile failed: ${info ?? "unknown"}`)
  }
  return shader
}

function createTexture(gl: WebGL2RenderingContext): WebGLTexture {
  const tex = gl.createTexture()
  if (!tex) throw new Error("failed to allocate texture")
  gl.bindTexture(gl.TEXTURE_2D, tex)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  return tex
}

class Compositor {
  private gl: WebGL2RenderingContext
  private program: WebGLProgram
  private frameTex: WebGLTexture
  private maskTex: WebGLTexture
  private bgTex: WebGLTexture
  private uFrame: WebGLUniformLocation
  private uMask: WebGLUniformLocation
  private uBg: WebGLUniformLocation
  canvas: OffscreenCanvas

  constructor(width: number, height: number) {
    this.canvas = new OffscreenCanvas(width, height)
    const gl = this.canvas.getContext("webgl2", {
      premultipliedAlpha: false,
      antialias: false,
    })
    if (!gl) throw new Error("WebGL2 not available on OffscreenCanvas")
    this.gl = gl

    const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
    const program = gl.createProgram()
    if (!program) throw new Error("failed to allocate program")
    gl.attachShader(program, vs)
    gl.attachShader(program, fs)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program)
      throw new Error(`program link failed: ${info ?? "unknown"}`)
    }
    gl.deleteShader(vs)
    gl.deleteShader(fs)
    this.program = program

    const positionBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    )
    const aPos = gl.getAttribLocation(program, "a_position")
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)

    this.frameTex = createTexture(gl)
    this.maskTex = createTexture(gl)
    this.bgTex = createTexture(gl)

    const uFrame = gl.getUniformLocation(program, "u_frame")
    const uMask = gl.getUniformLocation(program, "u_mask")
    const uBg = gl.getUniformLocation(program, "u_bg")
    if (!uFrame || !uMask || !uBg) {
      throw new Error("failed to look up sampler uniforms")
    }
    this.uFrame = uFrame
    this.uMask = uMask
    this.uBg = uBg

    gl.useProgram(program)
    gl.uniform1i(this.uFrame, 0)
    gl.uniform1i(this.uMask, 1)
    gl.uniform1i(this.uBg, 2)
    gl.viewport(0, 0, width, height)
  }

  setBackground(bitmap: ImageBitmap): void {
    const gl = this.gl
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, this.bgTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap)
  }

  setSize(width: number, height: number): void {
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width
      this.canvas.height = height
      this.gl.viewport(0, 0, width, height)
    }
  }

  uploadMask(data: Uint8Array, width: number, height: number): void {
    const gl = this.gl
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, this.maskTex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.R8,
      width,
      height,
      0,
      gl.RED,
      gl.UNSIGNED_BYTE,
      data,
    )
  }

  composite(frame: VideoFrame): void {
    const gl = this.gl
    gl.useProgram(this.program)

    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.frameTex)
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      frame as unknown as TexImageSource,
    )

    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, this.maskTex)

    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, this.bgTex)

    gl.drawArrays(gl.TRIANGLES, 0, 6)
  }

  destroy(): void {
    const gl = this.gl
    gl.deleteProgram(this.program)
    gl.deleteTexture(this.frameTex)
    gl.deleteTexture(this.maskTex)
    gl.deleteTexture(this.bgTex)
  }
}

// ─── Public processor handle ────────────────────────────────────────────────

export interface BackgroundProcessorHandle {
  processedTrack: MediaStreamVideoTrack
  destroy(): Promise<void>
}

export async function createBackgroundProcessor(
  inputTrack: MediaStreamVideoTrack,
  imageUrl: string,
): Promise<BackgroundProcessorHandle> {
  const settings = inputTrack.getSettings()
  const initialW = settings.width ?? 320
  const initialH = settings.height ?? 180

  const [segmenter, bgBitmap] = await Promise.all([
    getSegmenter(),
    getBackgroundBitmap(imageUrl),
  ])

  const compositor = new Compositor(initialW, initialH)
  compositor.setBackground(bgBitmap)

  const processor = new MediaStreamTrackProcessor({ track: inputTrack })
  const generator = new MediaStreamTrackGenerator({ kind: "video" })

  let lastTimestamp = 0

  const transformer = new TransformStream<VideoFrame, VideoFrame>({
    transform(frame, controller) {
      let outputFrame: VideoFrame | null = null
      try {
        const w = frame.displayWidth
        const h = frame.displayHeight
        compositor.setSize(w, h)

        // Timestamps must be monotonically increasing for segmentForVideo.
        let ts = Math.floor(frame.timestamp / 1000)
        if (ts <= lastTimestamp) ts = lastTimestamp + 1
        lastTimestamp = ts

        const result = segmenter.segmentForVideo(frame, ts)
        const masks = result.confidenceMasks
        // Selfie segmenter outputs two confidence masks; the second is the
        // person/foreground confidence. Defensive fallback to first.
        const personMask = masks?.[1] ?? masks?.[0]
        if (personMask) {
          const maskData = personMask.getAsUint8Array()
          compositor.uploadMask(maskData, personMask.width, personMask.height)
        }
        masks?.forEach((m) => m.close())

        compositor.composite(frame)

        outputFrame = new VideoFrame(compositor.canvas, {
          timestamp: frame.timestamp,
        })
      } catch {
        // Fall through; the input frame will be closed in finally and a
        // dropped frame is acceptable — the next one keeps the stream alive.
      } finally {
        frame.close()
      }
      if (outputFrame) controller.enqueue(outputFrame)
    },
  })

  const pipePromise = processor.readable
    .pipeThrough(transformer)
    .pipeTo(generator.writable)
    .catch(() => {
      /* stream closed or aborted; consumer will see track ended */
    })

  return {
    processedTrack: generator as unknown as MediaStreamVideoTrack,
    async destroy() {
      try {
        await processor.readable.cancel()
      } catch {
        /* noop */
      }
      try {
        await generator.writable.abort()
      } catch {
        /* noop */
      }
      try {
        generator.stop()
      } catch {
        /* noop */
      }
      compositor.destroy()
      await pipePromise
    },
  }
}
